import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CARRIERS, CARRIER_OTHER } from "@/lib/carriers";
import { Building2, Clock, Link2, Lock, Save, CreditCard, Euro, Truck, BellRing, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner, PageLoader } from "@/components/FindoSpinner";

export default function Settings() {
  const { t } = useLang();
  const { partner } = useAuth();
  const { establishments } = useEstablishment();
  const isStaff = partner?.role === "staff";

  // Establishment currently being configured.
  const [estId, setEstId] = useState("");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRem, setSavingRem] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [savingFees, setSavingFees] = useState(false);
  const [savingCarrier, setSavingCarrier] = useState(false);

  // form state
  const [reminderHours, setReminderHours] = useState(48);
  const [recUrl, setRecUrl] = useState("");
  const [senderId, setSenderId] = useState("");
  const [savingSender, setSavingSender] = useState(false);
  const [carrierSel, setCarrierSel] = useState("");
  const [carrierCustom, setCarrierCustom] = useState("");
  const [legalPrefs, setLegalPrefs] = useState({ prealert_15: true, prealert_7: true, deadline: true });
  const [savingLegalPref, setSavingLegalPref] = useState("");

  // Pick the target establishment: staff are locked to their own; owner/admin
  // default to the primary (or first) and can switch.
  const selectableEsts = useMemo(() => establishments || [], [establishments]);
  useEffect(() => {
    if (!selectableEsts.length) return;
    if (isStaff && partner?.member_establishment_id) {
      setEstId(partner.member_establishment_id);
      return;
    }
    setEstId((prev) => prev || (selectableEsts.find((e) => e.is_primary) || selectableEsts[0]).id);
  }, [selectableEsts, isStaff, partner?.member_establishment_id]);

  async function load(id) {
    if (!id) return;
    setLoading(true);
    try {
      const s = await api(`/establishments/${id}/payment-settings`);
      setSettings(s);
      setReminderHours(s.reminder_hours || 48);
      setRecUrl(s.external_recovery_url || "");
      setSenderId(partner?.sendcloud_sender_id ? String(partner.sendcloud_sender_id) : "");
      const dc = s.default_carrier || "";
      if (dc && !CARRIERS.includes(dc)) {
        setCarrierSel(CARRIER_OTHER);
        setCarrierCustom(dc);
      } else {
        setCarrierSel(dc);
        setCarrierCustom("");
      }
      setLegalPrefs(s.legal_email_prefs || { prealert_15: true, prealert_7: true, deadline: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (estId) load(estId); }, [estId]);

  const [carrierAvail, setCarrierAvail] = useState(null);
  async function loadCarrierAvailability(id) {
    if (!id) return;
    try { setCarrierAvail(await api(`/establishments/${id}/carrier-availability`)); }
    catch { setCarrierAvail(null); }
  }
  useEffect(() => { if (estId) loadCarrierAvailability(estId); }, [estId]);

  async function saveReminder() {
    setSavingRem(true);
    try {
      const val = Math.min(720, Math.max(1, parseInt(reminderHours, 10) || 48));
      const s = await api(`/establishments/${estId}/reminder-hours`, { method: "PUT", body: { reminder_hours: val } });
      setSettings(s); setReminderHours(s.reminder_hours);
      toast.success(t("hc.reminderDelaySaved"));
    } catch (e) { toast.error(e.message); }
    finally { setSavingRem(false); }
  }

  async function saveLinks() {
    setSavingLinks(true);
    try {
      const s = await api(`/establishments/${estId}/payment-links`, {
        method: "PUT",
        body: { external_recovery_url: recUrl.trim(), external_shipping_url: "" },
      });
      setSettings(s); setRecUrl(s.external_recovery_url || "");
      toast.success(t("hc.recoveryPaymentLinkSavedFor"));
    } catch (e) { toast.error(e.message); }
    finally { setSavingLinks(false); }
  }

  async function saveSender() {
    setSavingSender(true);
    try {
      const v = parseInt(senderId, 10);
      if (!v || v < 1) { toast.error(t("hc.invalidSendcloudId")); return; }
      await api(`/shipping/sendcloud/sender`, { method: "POST", body: { sender_id: v } });
      toast.success(t("hc.sendcloudSenderIdSaved"));
    } catch (e) { toast.error(e.message); }
    finally { setSavingSender(false); }
  }

  async function toggleRecoveryFees(enabled) {
    setSavingFees(true);
    try {
      const s = await api(`/establishments/${estId}/recovery-fees`, { method: "PUT", body: { enabled } });
      setSettings(s);
      toast.success(enabled ? t("hc.recoveryFeesEnabled") : t("hc.freeRecoveryFeesDisabled0"));
    } catch (e) { toast.error(e.message); }
    finally { setSavingFees(false); }
  }

  async function saveCarrier() {
    setSavingCarrier(true);
    try {
      const value = carrierSel === CARRIER_OTHER ? carrierCustom.trim() : (carrierSel || "");
      const s = await api(`/establishments/${estId}/default-carrier`, { method: "PUT", body: { default_carrier: value } });
      setSettings(s);
      toast.success(value ? `${t("hc.defaultCarrier")} : ${value}` : t("hc.defaultCarrierRemoved"));
      loadCarrierAvailability(estId);
    } catch (e) { toast.error(e.message); }
    finally { setSavingCarrier(false); }
  }

  const canEditLinks = settings?.can_edit_links;
  const currentEst = selectableEsts.find((e) => e.id === estId);

  async function toggleLegalPref(key, value) {
    const next = { ...legalPrefs, [key]: value };
    setLegalPrefs(next);
    setSavingLegalPref(key);
    try {
      const s = await api(`/establishments/${estId}/legal-email-prefs`, { method: "PUT", body: next });
      setLegalPrefs(s.legal_email_prefs || next);
      toast.success(t("hc.emailPreferencesUpdated"));
    } catch (e) {
      toast.error(e.message);
      setLegalPrefs(legalPrefs);
    } finally { setSavingLegalPref(""); }
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <PageHeader title={t("hc.establishmentSettings")} subtitle={t("hc.settingsSubtitle")} />

      {/* Establishment picker (owner/admin) */}
      {!isStaff && selectableEsts.length > 1 && (
        <div className="bg-white rounded-2xl border border-border p-4 card-shadow mb-5 flex items-center gap-3" data-testid="settings-est-picker">
          <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><Building2 size={18} /></span>
          <div className="flex-1 min-w-0">
            <Label className="text-xs text-slate-500">{t("hc.establishment")}</Label>
            <Select value={estId} onValueChange={setEstId}>
              <SelectTrigger className="mt-1 rounded-full" data-testid="settings-est-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {selectableEsts.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`settings-est-option-${e.id}`}>
                    {e.name}{e.is_primary ? ` (${t("hc.primary")})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {isStaff && currentEst && (
        <div className="bg-white rounded-2xl border border-border p-4 card-shadow mb-5 flex items-center gap-3" data-testid="settings-staff-est">
          <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><Building2 size={18} /></span>
          <div><p className="text-xs text-slate-500">{t("hc.yourEstablishment")}</p><p className="font-semibold text-slate-900">{currentEst.name}</p></div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-border card-shadow p-10 grid place-items-center text-slate-400"><FindoSpinner size={22} /></div>
      ) : (
        <div className="space-y-5">
          {/* Recovery fees toggle — owner/admin only */}
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-recovery-fees-card">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="h-9 w-9 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-600"><Euro size={18} /></span>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.recoveryFees")}</h3>
              {!canEditLinks && <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full"><Lock size={12} /> {t("hc.administratorOnly")}</span>}
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.enableOrDisableRecoveryFees")} <b>{currentEst?.name || t("hc.thisEstablishment")}</b>{t("hc.whenDisabledFeesDropTo")} <b>0 €</b> {t("hc.andRecoveryIs")} <b>{t("hc.free")}</b> {t("hc.toTheCustomer")}
            </p>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-border p-4">
              <div>
                <p className="font-semibold text-slate-800 text-sm">{settings?.recovery_fees_enabled ? t("hc.feesEnabled") : t("hc.freeRecovery0")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{settings?.recovery_fees_enabled ? t("hc.theCustomerPaysTheFees") : t("hc.noFeesAreChargedTo")}</p>
              </div>
              <div className="flex items-center gap-2">
                {savingFees && <FindoSpinner size={16} />}
                <Switch
                  checked={!!settings?.recovery_fees_enabled}
                  onCheckedChange={(v) => toggleRecoveryFees(v)}
                  disabled={!canEditLinks || savingFees}
                  data-testid="settings-recovery-fees-toggle"
                />
              </div>
            </div>
          </div>

          {/* Reminder delay — editable by everyone (incl. staff) */}
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-reminder-card">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="h-9 w-9 grid place-items-center rounded-xl bg-amber-500/10 text-amber-600"><Clock size={18} /></span>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.customerReminderDelay")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.ifThePartnerHasNot")}
            </p>
            <div className="flex items-end gap-3">
              <div>
                <Label className="text-xs text-slate-500">{t("hc.delayHours")}</Label>
                <Input type="number" min={1} max={720} value={reminderHours}
                  onChange={(e) => setReminderHours(e.target.value)}
                  className="mt-1 w-32 rounded-xl" data-testid="settings-reminder-input" />
              </div>
              <Button onClick={saveReminder} disabled={savingRem} className="rounded-xl font-semibold" data-testid="settings-reminder-save">
                {savingRem ? <FindoSpinner size={16} className="mr-1.5" /> : <Save size={16} className="mr-1.5" />} {t("hc.saveBtn")}
              </Button>
            </div>
          </div>

          {/* Default carrier — editable by everyone (incl. staff) */}
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-carrier-card">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><Truck size={18} /></span>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.defaultCarrier")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.thisCarrierWillBe")} <b>{t("hc.automaticallyPreSelected")}</b> {t("hc.forEveryNewShipmentFrom")} <b>{currentEst?.name || t("hc.thisEstablishment")}</b>{t("hc.youCanStillChangeIt")}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-slate-500">{t("hc.carrier")}</Label>
                <Select value={carrierSel} onValueChange={setCarrierSel}>
                  <SelectTrigger className="mt-1 w-56 rounded-full" data-testid="settings-carrier-select">
                    <SelectValue placeholder={t("hc.noneChooseForEachShipment")} />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`settings-carrier-opt-${c}`}>{c}</SelectItem>
                    ))}
                    <SelectItem value={CARRIER_OTHER} data-testid="settings-carrier-opt-other">{t("hc.other2")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {carrierSel === CARRIER_OTHER && (
                <div>
                  <Label className="text-xs text-slate-500">{t("hc.carrierOther")}</Label>
                  <Input value={carrierCustom} onChange={(e) => setCarrierCustom(e.target.value)}
                    placeholder={t("hc.eGColisPriv")} className="mt-1 w-56 rounded-xl" data-testid="settings-carrier-custom" />
                </div>
              )}
              <Button onClick={saveCarrier} disabled={savingCarrier} className="rounded-xl font-semibold" data-testid="settings-carrier-save">
                {savingCarrier ? <FindoSpinner size={16} className="mr-1.5" /> : <Save size={16} className="mr-1.5" />} {t("hc.saveBtn")}
              </Button>
            </div>
            {carrierAvail?.checked && carrierAvail?.default_carrier && carrierAvail?.unavailable_routes?.length > 0 && (
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3.5 text-sm text-amber-800" data-testid="settings-carrier-unavailable-alert">
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
                <div>
                  <b>{carrierAvail.default_carrier}</b> {t("hc.isNotEnabledOnYour")}
                  <ul className="mt-1.5 space-y-1">
                    {carrierAvail.unavailable_routes.map((r) => (
                      <li key={r.route} className="text-amber-700">
                        <b>{r.label}</b> ({r.route}) — disponibles : {r.available_carriers?.length ? r.available_carriers.join(", ") : t("hc.none")}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1.5 text-amber-700">Pour utiliser {carrierAvail.default_carrier} sur ces routes, activez un contrat {carrierAvail.default_carrier} dans votre compte Sendcloud, ou choisissez un transporteur disponible ci-dessus.</div>
                </div>
              </div>
            )}
            {carrierAvail?.checked && carrierAvail?.default_carrier && carrierAvail?.default_available_everywhere === true && (
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="settings-carrier-available-alert">
                <Truck size={16} className="shrink-0" /> <b>{carrierAvail.default_carrier}</b> {t("hc.isAvailableOnSendcloudFor")}
              </div>
            )}
            {carrierAvail && carrierAvail.configured === false && carrierAvail.default_carrier && (
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-600" data-testid="settings-carrier-nocheck-alert">
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-slate-400" />
                <div>{t("hc.carrierAvailabilityCannotBeChecked")}</div>
              </div>
            )}
          </div>

          <PartnerBillingHistory />

          {/* Legal-retention email notification preferences — everyone (incl. staff) */}
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-legal-email-prefs-card">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="h-9 w-9 grid place-items-center rounded-xl bg-orange-500/10 text-orange-600"><BellRing size={18} /></span>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.emailNotificationPreferences")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.chooseTheLegalRetentionAlerts")} <b>{currentEst?.name || t("hc.thisEstablishment")}</b>.
              {t("hc.disablingAnEmail")} <b>{t("hc.doesNotHide")}</b> {t("hc.theBannerDisplayedInThe")}
            </p>
            <div className="space-y-2.5">
              {[
                { key: "prealert_15", title: t("hc.j15EarlyReminder"), desc: t("hc.15DaysBeforeAnItem") },
                { key: "prealert_7", title: t("hc.j7EarlyReminder"), desc: t("hc.7DaysBeforeAnItem") },
                { key: "deadline", title: t("hc.deadlineAlert"), desc: t("hc.theDayTheLegalRetention") },
              ].map((row) => (
                <div key={row.key} className="flex items-center justify-between rounded-xl bg-slate-50 border border-border p-4" data-testid={`legal-pref-row-${row.key}`}>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{row.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{row.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {savingLegalPref === row.key && <FindoSpinner size={16} />}
                    <Switch
                      checked={legalPrefs[row.key] !== false}
                      onCheckedChange={(v) => toggleLegalPref(row.key, v)}
                      disabled={!!savingLegalPref}
                      data-testid={`legal-pref-toggle-${row.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* External payment links — owner/admin only */}
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-links-card">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><Link2 size={18} /></span>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.externalPaymentLinks")}</h3>
              {!canEditLinks && <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full" data-testid="settings-links-locked"><Lock size={12} /> {t("hc.administratorOnly")}</span>}
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {t("hc.linkedTo")} <b>{currentEst?.name || t("hc.thisEstablishment")}</b> {t("hc.onlyEachSiteManagesIts")}
              {t("hc.leaveTheFieldsEmptyTo")}
            </p>

            {canEditLinks ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1.5"><CreditCard size={13} /> {t("hc.recoveryPaymentLink")}</Label>
                  <Input value={recUrl} onChange={(e) => setRecUrl(e.target.value)} placeholder="https://votre-systeme-de-paiement.com/recuperation"
                    className="mt-1 rounded-xl" data-testid="settings-recovery-url" />
                  <p className="mt-1 text-xs text-slate-400">{t("hc.shippingFeesAreHandledAutomatically")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={saveLinks} disabled={savingLinks} className="rounded-xl font-semibold" data-testid="settings-links-save">
                    {savingLinks ? <FindoSpinner size={16} className="mr-1.5" /> : <Save size={16} className="mr-1.5" />} {t("hc.saveLink")}
                  </Button>
                </div>
                <div className="pt-4 border-t border-border">
                  <Accordion type="single" collapsible className="rounded-xl border border-border bg-slate-50 px-4" data-testid="sender-id-guide">
                    <AccordionItem value="sender-id" className="border-b-0">
                      <AccordionTrigger className="text-slate-800 font-semibold" data-testid="sender-id-guide-trigger">
                        {t("hc.howConfigureSenderId")}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600" data-testid="sender-id-guide-content">
                        <ol className="space-y-3 list-decimal pl-5 marker:font-bold marker:text-findo-indigo">
                          <li>
                            <b>{t("hc.contactFindoToGetYour")}</b> {t("hc.findoRegistersTheAddressOf")}
                            {t("hc.establishmentInItsSendcloudAccount")}
                            (<i>sender_id</i>{t("hc.field")}
                          </li>
                          <li>
                            <b>{t("hc.pasteThisSenderIdIn")}</b>{t("hc.inTheField")}
                            « Identifiant expéditeur Sendcloud (sender_id) » ci-dessous, puis <b>{t("hc.save2")}</b>.
                          </li>
                          <li className="text-findo-red font-semibold">
                            {t("hc.thisStepIsRequiredFor")}
                            {t("hc.automaticallyWhenShipping")}
                          </li>
                        </ol>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Label className="mt-4 text-xs text-slate-500 flex items-center gap-1.5"><CreditCard size={13} /> {t("hc.sendcloudSenderIdSenderId")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={senderId} onChange={(e) => setSenderId(e.target.value)} type="number" min="1"
                      placeholder="Ex. 123456" className="rounded-xl" data-testid="settings-sendcloud-sender" />
                    <Button onClick={saveSender} disabled={savingSender} variant="outline" className="rounded-xl font-semibold" data-testid="settings-sendcloud-save">
                      {savingSender ? <FindoSpinner size={16} /> : <Save size={16} />}
                    </Button>
                  </div>
                  {!senderId && (
                    <p className="mt-1.5 text-xs text-findo-danger font-semibold flex items-center gap-1" data-testid="settings-sendcloud-warning">
                      <AlertTriangle size={13} /> {t("hc.thisFieldIsRequiredFor")}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 border border-border p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="settings-links-readonly">
                <Lock size={15} /> {t("hc.onlyTheOwnerOrAn")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerBillingHistory() {
  const { t } = useLang();
  const [data, setData] = React.useState({ count: 0, invoices: [] });
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    api("/monthly-invoices/mine")
      .then(setData)
      .catch(() => setData({ count: 0, invoices: [] }))
      .finally(() => setLoading(false));
  }, []);
  const STATUS = {
    paid: { label: t("hc.paid"), cls: "bg-emerald-100 text-emerald-700" },
    failed: { label: t("hc.failed"), cls: "bg-red-100 text-red-700" },
    unpaid: { label: t("hc.upcoming"), cls: "bg-amber-100 text-amber-700" },
  };
  return (
    <div className="bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="settings-billing-card">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><CreditCard size={18} /></span>
        <h3 className="font-display font-bold text-lg text-slate-900">{t("hc.myMonthlyInvoices")}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t("hc.historyOfYourMonthlySubscriptions")}</p>
      {loading ? (
        <PageLoader label={t("hc.loading")} className="py-8" size={32} />
      ) : data.invoices.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="billing-empty">{t("hc.noInvoiceYet")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billing-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-border">
                <th className="py-2 pr-3">{t("hc.period")}</th><th className="py-2 pr-3">{t("hc.amount")}</th>
                <th className="py-2 pr-3">{t("hc.status")}</th><th className="py-2 pr-3">{t("hc.paidOn")}</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => {
                const s = STATUS[inv.status] || STATUS.unpaid;
                return (
                  <tr key={inv.id} className="border-b border-border/60" data-testid={`billing-row-${inv.id}`}>
                    <td className="py-2.5 pr-3 font-medium text-slate-800">{inv.period}</td>
                    <td className="py-2.5 pr-3">{Number(inv.total_ttc || 0).toFixed(2)} € TTC</td>
                    <td className="py-2.5 pr-3"><span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span></td>
                    <td className="py-2.5 pr-3 text-slate-500">{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString(document.documentElement.lang || "fr-FR") : "—"}</td>
                    <td className="py-2.5">
                      {inv.status !== "paid" && inv.access_token && (
                        <a href={`${API}/monthly-invoices/${inv.access_token}.pdf`} target="_blank" rel="noreferrer" className="text-findo-indigo font-semibold text-xs hover:underline" data-testid={`billing-pdf-${inv.id}`}>{t("hc.pdfInvoice")}</a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
